import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { UnitAccessVM, UserHlp } from 'src/lib/fliko/helpers/user.helper';
import { CommunityTenancyData } from 'src/lib/fliko/models/agreements/communityTenancy.data';
import { ResidentData, ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { ParkingData } from 'src/lib/fliko/models/units/parking.data';
import { StorageData } from 'src/lib/fliko/models/units/storage.data';
import { RentalUnitType, RentalUnit } from 'src/lib/fliko/models/units/unit.data';
import { UnitCode } from 'src/lib/fliko/models/units/unitCode';
import { ShareData } from 'src/lib/fliko/models/users/share.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { UserHelper } from 'src/app/shared/helpers/user-helper';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ReckoningsService } from 'src/app/view/reckonings/services/reckonings.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { UserCommList, UserCommunityTabData } from './models/users-table.data';
import { UnitResidentViewModel, UnitCommResidencyViewModel, TenancyViewModel } from './models/users-view-models';
import { AgreementService } from './agreement.service';
import { ResidentService } from './resident.service';
import { ShareService } from './share.service';
import { UnitService } from './unit.service';
import { UserService } from './user.service';

interface ResidentsAffiliation {
  active: UserCommList.ResidentElement[];
  inactive: UserCommList.ResidentElement[];
}

@Injectable({
  providedIn: 'root'
})
export class UsersCommDataService implements OnDestroy {
  private landlordTabData = new BehaviorSubject<UserCommList.ResidentElement[] | null>(null);
  private tenantTabData = new BehaviorSubject<UserCommList.ResidentElement[] | null>(null);
  private unassignedResidentTabData = new BehaviorSubject<UserCommList.ResidentElement[] | null>(null);
  private apartmentsTabData = new BehaviorSubject<UserCommList.ApartmentElement[] | null>(null);
  private parkingTabData = new BehaviorSubject<UserCommList.ParkingStorageElement[] | null>(null);
  private storageTabData = new BehaviorSubject<UserCommList.ParkingStorageElement[] | null>(null);

  private apartmentResidencyData = new BehaviorSubject<Map<string, UnitCommResidencyViewModel> | null>(null);
  private parkingResidencyData = new BehaviorSubject<Map<string, UnitCommResidencyViewModel> | null>(null);
  private storageResidencyData = new BehaviorSubject<Map<string, UnitCommResidencyViewModel> | null>(null);
  private tenanciesData = new BehaviorSubject<TenancyViewModel[] | null>(null);

  private readonly locHelper = new LocalizationHelper(this.ls);

  private subscription$ = new Subscription();

  constructor(
    private userService: UserService,
    private unitService: UnitService,
    private residentService: ResidentService,
    private shareService: ShareService,
    private agreementService: AgreementService,
    private reckoningService: ReckoningsService,
    private ls: LocalizationService,
    private ctxDS: ContextDataService,
    private errorHandler: ErrorHandlingService,
    @Optional() @SkipSelf() thisService?: UsersCommDataService
  ) {
    if (thisService) throw new Error('Multiple instances of UsersCommDataService');
    this.subscription$.add(this.getTabData$().subscribe((data) => this.processData(data)));
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public getLandlordsTabData$(): Observable<UserCommList.ResidentElement[] | null> {
    return this.landlordTabData.asObservable();
  }

  public getTenantsTabData$(): Observable<UserCommList.ResidentElement[] | null> {
    return this.tenantTabData.asObservable();
  }

  public getUnassignedResidentsTabData$(): Observable<UserCommList.ResidentElement[] | null> {
    return this.unassignedResidentTabData.asObservable();
  }

  public getUnitsTabData$(
    type: RentalUnitType
  ): Observable<(UserCommList.ApartmentElement | UserCommList.ParkingStorageElement)[] | null> {
    switch (type) {
      case RentalUnitType.APARTMENT:
        return this.apartmentsTabData.asObservable();
      case RentalUnitType.PARKING:
        return this.parkingTabData.asObservable();
      case RentalUnitType.STORAGE:
        return this.storageTabData.asObservable();
    }
  }

  public getLandlordTabData(id: string): UserCommList.ResidentElement | undefined {
    return this.landlordTabData.value?.find((x) => x.id == id);
  }

  public getTenantTabData(id: string): UserCommList.ResidentElement | undefined {
    return this.tenantTabData.value?.find((x) => x.id == id);
  }

  public getUnitTabData(
    id: string,
    type: RentalUnitType
  ): UserCommList.ApartmentElement | UserCommList.ParkingStorageElement | undefined {
    switch (type) {
      case RentalUnitType.APARTMENT:
        return this.apartmentsTabData.value?.find((x) => x.id == id);
      case RentalUnitType.PARKING:
        return this.parkingTabData.value?.find((x) => x.id == id);
      case RentalUnitType.STORAGE:
        return this.storageTabData.value?.find((x) => x.id == id);
    }
  }

  public getUnitsResidencyData$(type: RentalUnitType, ids?: string[]): Observable<UnitCommResidencyViewModel[]> {
    const mapFunc = map((data: Map<string, UnitCommResidencyViewModel> | null) => {
      let ret: UnitCommResidencyViewModel[] = [];
      if (data) {
        if (ids) {
          ids.forEach((id) => {
            const unit = data.get(id);
            if (unit) ret.push(unit);
          });
        } else {
          ret = Array.from(data.values());
        }
      }
      return ret;
    });
    switch (type) {
      case RentalUnitType.APARTMENT:
        return this.apartmentResidencyData.pipe(mapFunc);
      case RentalUnitType.PARKING:
        return this.parkingResidencyData.pipe(mapFunc);
      case RentalUnitType.STORAGE:
        return this.storageResidencyData.pipe(mapFunc);
    }
  }

  public getTenanciesData$(): Observable<TenancyViewModel[] | null> {
    return this.tenanciesData.asObservable();
  }

  private getTabData$(): Observable<UserCommunityTabData> {
    // HACK combine latest looses types if more than 6 args
    return combineLatest([
      combineLatest([
        this.residentService.getResidents$(),
        this.shareService.getShares$(),
        this.agreementService.getCommAgreement$(),
        this.userService.getUsers$()
      ]),
      this.unitService.getApartments$(),
      this.unitService.getParkings$(),
      this.unitService.getStorages$(),
      this.ctxDS.currentEstate$,
      this.reckoningService.getUnitBalances$()
    ]).pipe(
      map(([[residents, shares, communityTenancy, users], apartments, parkings, storages, estate, unitBalances]) => {
        const estateShareSum = estate?.totalShares ?? 0;
        const apBankAcc = new Map<string, string>();
        const parBankAcc = new Map<string, string>();
        const stoBankAcc = new Map<string, string>();

        unitBalances.forEach((balance) => {
          const map =
            balance.unitType == RentalUnitType.APARTMENT
              ? apBankAcc
              : balance.unitType == RentalUnitType.PARKING
                ? parBankAcc
                : stoBankAcc;
          const accounts: string[] = [];
          for (const currency in balance.balances) {
            const b = balance.balances[currency];
            if (b.bankAccount) accounts.push(b.bankAccount);
          }
          if (accounts.length) map.set(balance.unitId, accounts.join(', '));
        });

        return {
          apartments,
          residents,
          shares,
          parkings,
          storages,
          estateShareSum,
          communityTenancy,
          apBankAcc,
          parBankAcc,
          stoBankAcc,
          usersMap: MapHlp.idMap(users)
        };
      })
    );
  }

  private processData(data: UserCommunityTabData): void {
    const apResidencyMap = new Map<string, UnitCommResidencyViewModel>();
    const parResidencyMap = new Map<string, UnitCommResidencyViewModel>();
    const stoResidencyMap = new Map<string, UnitCommResidencyViewModel>();
    this.prepareAllResidents(data, apResidencyMap, parResidencyMap, stoResidencyMap);
    this.apartmentsTabData.next(this.prepareApartmentData(data));
    this.parkingTabData.next(this.prepareParkingStorageData(data, data.parkings, RentalUnitType.PARKING));
    this.storageTabData.next(this.prepareParkingStorageData(data, data.storages, RentalUnitType.STORAGE));
    this.apartmentResidencyData.next(apResidencyMap);
    this.parkingResidencyData.next(parResidencyMap);
    this.storageResidencyData.next(stoResidencyMap);
  }

  private prepareLandlordData(
    userData: UserCommunityTabData,
    apResideMap: Map<string, UnitCommResidencyViewModel>,
    parResideMap: Map<string, UnitCommResidencyViewModel>,
    stoResideMap: Map<string, UnitCommResidencyViewModel>,
    touchedIds: Set<string>
  ): ResidentsAffiliation {
    const apartmentMap = MapHlp.idMap(userData.apartments);
    const parkingMap = MapHlp.idMap(userData.parkings);
    const storageMap = MapHlp.idMap(userData.storages);
    const residentsMap = MapHlp.idMap(userData.residents);
    const residentElementMap = new Map<string, UserCommList.ResidentElement>();
    const lh = this.locHelper;

    function prepareResidentVM(r: ResidentData, unit: RentalUnit, share?: ShareData, marital?: boolean): UnitResidentViewModel {
      return {
        id: r.id,
        name: ToString.fullName(r),
        type: ResidentType.LANDLORD,
        share: share ? lh.shareOfTotal(share.shareCount ?? 0, unit.shareInEstate ?? 1) : undefined,
        maritalCoOwnership: marital
      };
    }

    function prepareUnitResidencyVM(
      unit: RentalUnit,
      type: RentalUnitType,
      rVM: UnitResidentViewModel,
      bankAccountMap: Map<string, string>
    ): UnitCommResidencyViewModel {
      const anyUnit = unit as any;
      return {
        bankAccount: bankAccountMap.get(unit.id) ?? '',
        residents: [rVM],
        id: unit.id,
        type,
        area: anyUnit.areaInSquareMeters ?? anyUnit.area ?? 0,
        unitName: ToString.shortNumber(unit),
        unitCode: UnitCode.fromRentalUnit(unit) ?? '',
        unit
      };
    }

    function handleShares(id: string, share: ShareData, marital: boolean) {
      const landlord = residentsMap.get(id);
      if (landlord) {
        const residentElement =
          residentElementMap.get(id) ?? UserCommList.emptyResidentElement(landlord, userData.usersMap, ResidentType.LANDLORD);
        if (!residentElement.shares) residentElement.shares = [];
        residentElement.shares.push(share);

        function handleSharesParkingStorage(
          unitMap: Map<string, StorageData | ParkingData>,
          residencyMap: Map<string, UnitCommResidencyViewModel>,
          bankMap: Map<string, string>
        ) {
          const unitId = share.unitId;
          const type = share.unitType;
          const unit = unitMap.get(unitId);
          if (unit) {
            if (type == RentalUnitType.PARKING) residentElement.parkings.push(unit as ParkingData);
            else residentElement.storages.push(unit as StorageData);

            const residentVM = prepareResidentVM(landlord!, unit, share, marital);
            let unitResident = residencyMap.get(unitId);
            if (unitResident) unitResident.residents.push(residentVM);
            else {
              unitResident = prepareUnitResidencyVM(unit, type, residentVM, bankMap);
              residencyMap.set(unitId, unitResident);
            }
          }
        }

        switch (share.unitType) {
          case RentalUnitType.APARTMENT: {
            const apartment = apartmentMap.get(share.unitId);
            if (apartment) {
              const aResidentVM = prepareResidentVM(landlord, apartment, share, marital);

              residentElement.apartments.push(apartment);
              const parkings = userData.parkings.filter((p) => p.apartmentId == apartment.id);
              residentElement.parkings.push(...parkings);
              parkings.forEach((p) => {
                const pResidentVM = prepareResidentVM(landlord, p);
                let parkingResident = parResideMap.get(p.id);
                if (parkingResident) parkingResident.residents.push(pResidentVM);
                else {
                  parkingResident = prepareUnitResidencyVM(p, RentalUnitType.PARKING, pResidentVM, userData.parBankAcc);
                  parResideMap.set(p.id, parkingResident);
                }
              });
              const storages = userData.storages.filter((s) => s.apartmentId == apartment.id);
              residentElement.storages.push(...storages);
              storages.forEach((s) => {
                const sResidentVM = prepareResidentVM(landlord, s);
                let storageResident = stoResideMap.get(s.id);
                if (storageResident) storageResident.residents.push(sResidentVM);
                else {
                  storageResident = prepareUnitResidencyVM(s, RentalUnitType.STORAGE, sResidentVM, userData.stoBankAcc);
                  stoResideMap.set(s.id, storageResident);
                }
              });

              let unitResident = apResideMap.get(apartment.id);
              if (unitResident) unitResident.residents.push(aResidentVM);
              else {
                unitResident = prepareUnitResidencyVM(apartment, share.unitType, aResidentVM, userData.apBankAcc);
                apResideMap.set(apartment.id, unitResident);
              }
            }
            break;
          }
          case RentalUnitType.PARKING:
            handleSharesParkingStorage(parkingMap, parResideMap, userData.parBankAcc);
            break;
          case RentalUnitType.STORAGE:
            handleSharesParkingStorage(storageMap, stoResideMap, userData.stoBankAcc);
            break;
        }
        residentElementMap.set(id, residentElement);
      }
    }

    //* ------------------------------------------ Real logic
    userData.shares.forEach((share) => {
      handleShares(share.ownerId, share, !!share.coOwnerId);
      touchedIds.add(share.ownerId);
      if (share.coOwnerId) {
        handleShares(share.coOwnerId, share, true);
        touchedIds.add(share.coOwnerId);
      }
    });

    // TODO: archive shares, when exist

    const residentElements = Array.from(residentElementMap.values());
    const active: UserCommList.ResidentElement[] = [];
    const inactive: UserCommList.ResidentElement[] = [];
    residentElements.forEach((e) => {
      if (e.apartments.length || e.parkings.length || e.storages.length) {
        active.push(e);
        e.displayShare = this.locHelper.shareOfTotal(
          e.shares?.reduce((acc, x) => acc + x.shareCount, 0) ?? 0,
          userData.estateShareSum
        );
      } else {
        inactive.push({ ...e, archiveType: ResidentType.LANDLORD });
      }
    });

    return { active: this.sortResidents(active), inactive };
  }

  private prepareTenantData(
    userData: UserCommunityTabData,
    apResideMap: Map<string, UnitCommResidencyViewModel>,
    parResideMap: Map<string, UnitCommResidencyViewModel>,
    stoResideMap: Map<string, UnitCommResidencyViewModel>,
    touchedIds: Set<string>
  ): ResidentsAffiliation {
    const apartmentsMap = MapHlp.idMap(userData.apartments);
    const parkingsMap = MapHlp.idMap(userData.parkings);
    const storagesMap = MapHlp.idMap(userData.storages);
    const residentsMap = MapHlp.idMap(userData.residents);
    const residentElementMap = new Map<string, UserCommList.ResidentElement>();
    const tenancies: TenancyViewModel[] = [];

    function prepareResidentVM(r: ResidentData, ten: CommunityTenancyData, currentTenancy: boolean): UnitResidentViewModel {
      const currentTenancies: UnitAccessVM[] = [];
      const previousTenancies: UnitAccessVM[] = [];
      const tenancyVM: UnitAccessVM = {
        startDate: ten.startDate,
        endDate: ten.endDate,
        cancelDate: ten.cancelDate,
        creationDate: ten.creationDate,
        creatorUserName: ToString.fullName(userData.usersMap.get(ten.creatorUserId))
      };
      if (currentTenancy) currentTenancies.push(tenancyVM);
      else previousTenancies.push(tenancyVM);

      return {
        id: r.id,
        name: ToString.fullName(r),
        type: ResidentType.TENANT,
        currentTenancies,
        previousTenancies
      };
    }

    //* --------------------------- real logic
    userData.communityTenancy.forEach((tenancy) => {
      const tenant = residentsMap.get(tenancy.tenantId);
      if (tenant) {
        const landlord = residentsMap.get(tenancy.landlordId);
        const creatorUserName = ToString.fullName(userData.usersMap.get(tenancy.creatorUserId));
        const tenancyVM: TenancyViewModel = {
          id: tenancy.id,
          tenant,
          landlord: landlord ?? null,
          apartment: null,
          parkings: [],
          storages: [],
          creationDate: tenancy.creationDate,
          creatorUserName,
          startDate: tenancy.startDate,
          endDate: tenancy.endDate,
          cancelDate: tenancy.cancelDate
        };
        const residentElement =
          residentElementMap.get(tenant.id) ?? UserCommList.emptyResidentElement(tenant, userData.usersMap, ResidentType.TENANT);
        const isCurrentTenancy = UserHlp.isCurrentCommunityTenancy(tenancy);

        function handleUnit(
          unitId: string,
          unitType: RentalUnitType,
          unitMap: Map<string, ApartmentData | ParkingData | StorageData>,
          resideMap: Map<string, UnitCommResidencyViewModel>,
          bankAccountsMap: Map<string, string>
        ) {
          const unit = unitMap.get(unitId);
          if (unit) {
            let area = 0;
            switch (unitType) {
              case RentalUnitType.APARTMENT:
                if (!residentElement.apartments.find((a) => a.id == unitId)) {
                  const apartment = unit as ApartmentData;
                  tenancyVM.apartment = apartment;
                  if (isCurrentTenancy) residentElement.apartments.push(apartment);
                  else residentElement.archiveApartments.push(apartment);
                  area = apartment.area ?? 0;
                }
                break;
              case RentalUnitType.PARKING: {
                const parking = unit as ParkingData;
                tenancyVM.parkings.push(parking);
                if (!residentElement.parkings.find((p) => p.id == unitId)) {
                  if (isCurrentTenancy) residentElement.parkings.push(parking);
                  else residentElement.archiveParkings.push(parking);
                }
                break;
              }
              case RentalUnitType.STORAGE: {
                const storage = unit as StorageData;
                tenancyVM.storages.push(storage);
                if (!residentElement.storages.find((s) => s.id == unitId)) {
                  if (isCurrentTenancy) residentElement.storages.push(storage);
                  else residentElement.archiveStorages.push(storage);
                }
                area = storage.area ?? 0;
                break;
              }
            }

            let unitResidency = resideMap.get(unitId);
            const residentVM = prepareResidentVM(tenant!, tenancy, isCurrentTenancy);
            if (unitResidency) {
              // BUG https://fliko.atlassian.net/browse/FLIKO-1613 fixed, if error still exists, start checking here
              const foundRVM = unitResidency.residents.find((r) => r.id == tenant!.id && r.type == ResidentType.TENANT);
              if (!foundRVM) {
                unitResidency.residents.push(residentVM);
              } else {
                const tenancyVM: UnitAccessVM = {
                  startDate: tenancy.startDate,
                  endDate: tenancy.endDate,
                  cancelDate: tenancy.cancelDate,
                  creationDate: tenancy.creationDate,
                  creatorUserName
                };
                if (isCurrentTenancy) foundRVM.currentTenancies!.push(tenancyVM);
                else foundRVM.previousTenancies!.push(tenancyVM);
              }
            } else {
              unitResidency = {
                bankAccount: bankAccountsMap.get(unit.id) ?? '',
                residents: [residentVM],
                id: unitId,
                type: unitType,
                area,
                unitName: ToString.shortNumber(unit),
                unitCode: '',
                unit
              };
              resideMap.set(unitId, unitResidency);
            }
          }
        } // ? function end

        if (!tenancy.units) this.errorHandler.showSilentWarning(`Tenancy ${tenancy.id} has no units`);

        tenancy.units?.forEach((unit) => {
          switch (unit.type) {
            case RentalUnitType.APARTMENT:
              handleUnit(unit.id, unit.type, apartmentsMap, apResideMap, userData.apBankAcc);
              break;
            case RentalUnitType.PARKING:
              handleUnit(unit.id, unit.type, parkingsMap, parResideMap, userData.parBankAcc);
              break;
            case RentalUnitType.STORAGE:
              handleUnit(unit.id, unit.type, storagesMap, stoResideMap, userData.stoBankAcc);
              break;
          }
          residentElementMap.set(tenancy.tenantId, residentElement);
        });
        tenancies.push(tenancyVM);
      }
    });
    this.tenanciesData.next(tenancies);
    const tenants = Array.from(residentElementMap.values());

    const active: UserCommList.ResidentElement[] = [];
    const inactive: UserCommList.ResidentElement[] = [];
    tenants.forEach((t) => {
      if (t.apartments.length || t.storages.length || t.parkings.length) active.push(t);
      else inactive.push({ ...t, archiveType: ResidentType.TENANT });
      touchedIds.add(t.id);
    });
    return { active: this.sortResidents(active), inactive };
  }

  private prepareAllResidents(
    userData: UserCommunityTabData,
    apResideMap: Map<string, UnitCommResidencyViewModel>,
    parResideMap: Map<string, UnitCommResidencyViewModel>,
    stoResideMap: Map<string, UnitCommResidencyViewModel>
  ): void {
    const touchedIds = new Set<string>();
    const landlords = this.prepareLandlordData(userData, apResideMap, parResideMap, stoResideMap, touchedIds);
    const tenants = this.prepareTenantData(userData, apResideMap, parResideMap, stoResideMap, touchedIds);

    this.landlordTabData.next(landlords.active);
    this.tenantTabData.next(tenants.active);
    const unassigned: UserCommList.ResidentElement[] = [];
    userData.residents.forEach((resident) => {
      if (!touchedIds.has(resident.id)) {
        unassigned.push({
          id: resident.id,
          resident,
          creator: userData.usersMap.get(resident.creatorUserId ?? '') ?? null,
          type: ResidentType.UNASSIGNED,
          archiveType: ResidentType.UNASSIGNED,
          apartments: [],
          parkings: [],
          storages: [],
          archiveApartments: [],
          archiveParkings: [],
          archiveStorages: [],
          displayShare: null,
          shares: null
        });
      }
    });
    // TODO: Merge residents if duplicates in tenant/landlord
    landlords.inactive.forEach((l) => unassigned.push(l));
    tenants.inactive.forEach((t) => unassigned.push(t));
    this.unassignedResidentTabData.next(this.sortResidents(unassigned));
  }

  private prepareApartmentData(userData: UserCommunityTabData): UserCommList.ApartmentElement[] {
    return userData.apartments.map((apartment) => {
      const shares = userData.shares.filter((s) => s.unitId == apartment.id);
      const landlords = userData.residents.filter((l) => shares.find((s) => s.ownerId == l.id || s.coOwnerId == l.id));

      return {
        id: apartment.id,
        unit: apartment,
        type: RentalUnitType.APARTMENT,
        shares,
        displayShare: this.locHelper.shareOfTotal(apartment.shareInEstate ?? 0, userData.estateShareSum),
        landlords,
        parkings: userData.parkings
          .filter((x) => x.apartmentId == apartment.id)
          .map((x) => UserHelper.unitToRentalUnitVM(x, RentalUnitType.PARKING)),
        storages: userData.storages
          .filter((x) => x.apartmentId == apartment.id)
          .map((x) => UserHelper.unitToRentalUnitVM(x, RentalUnitType.STORAGE))
      };
    });
  }

  private prepareParkingStorageData(
    userData: UserCommunityTabData,
    source: (ParkingData | StorageData)[],
    type: RentalUnitType
  ): UserCommList.ParkingStorageElement[] {
    return source.map((unit) => {
      const shares = userData.shares.filter((s) => s.unitId == unit.id);
      const displayShare = shares.length
        ? this.locHelper.shareOfTotal(unit.shareInEstate ?? 0, userData.estateShareSum)
        : this.ls.translate('label.notApplicable');

      const apartment = userData.apartments.find((x) => x.id == unit.apartmentId);
      const aptShares = apartment ? userData.shares.filter((s) => s.unitId == apartment.id) : [];

      const landlords = userData.residents.filter((r) =>
        shares.concat(aptShares).find((s) => s.ownerId == r.id || s.coOwnerId == r.id)
      );
      if (shares?.length && apartment) console.warn(`${type} ${unit.id} has both shares and parent apartment!`);

      return { id: unit.id, unit, type, shares, displayShare, landlords, apartment };
    });
  }

  private sortResidents(array: UserCommList.ResidentElement[]): UserCommList.ResidentElement[] {
    return array.sort((a, b) => {
      const aN = `${a.resident.lastName ?? ''} ${a.resident.name}`;
      const bN = `${b.resident.lastName ?? ''} ${b.resident.name}`;
      return aN.localeCompare(bN);
    });
  }
}
